import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import contactBorder from "@assets/images/borders/contactBorder.webp";
import type { IAddress, ICheckoutAddress } from "@src/types/AppTypes";
import { joinAddress } from "@utils/AddressHelper";
import TransparentLoader from "@components/Loaders/TransparentLoader";
import AddAddressWithout from "./AddAddressWithout";
import shippingBorder from "@assets/images/borders/shippingBorder.webp";
import shippingAddressBorder from "@assets/images/borders/shippingAddressBorder.png";
import { UserContext } from "@src/context/UserContext";
import AddAddress from "./AddAddress";
import useOnclickOutside from "react-cool-onclickoutside";
import { Tooltip } from "react-tooltip";

const CheckoutAddress: React.FC<ICheckoutAddress> = ({
  address,
  setAddress,
  showBillingCheckbox,
  showBillingAddress,
  setBillingAddress,
  title,
  id,
  errors,
  errorType,
  addressOptions,
  addresses,
  setAddresses,
  loading = false,
  selectValue,
  setSelectValue,
  isShippingPrimary,
  setIsShippingPrimary,
  isBillingPrimary,
  setIsBillingPrimary,
}) => {
  const user = useContext(UserContext);
  const [isAddNew, setIsAddNew] = useState<boolean>(false);
  const [isShowMoreAddress, setIsShowMoreAddress] = useState<boolean>(false);

  // for close add address modal on outside click
  const addAddressModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document
        .getElementById("checkout-address-form")
        ?.classList.add("form-shake");
    },
    {
      refs: [addAddressModalRef],
      ignoreClass: ["swal2-container", "no-shake-animation"],
    }
  );

  const updateAddresses = useCallback(
    (address: IAddress) => {
      let newAddresses = [...addresses];
      if (address.is_primary) {
        newAddresses = newAddresses.map((item) => {
          item.is_primary = false;
          return item;
        });
      }
      if (id === "shipping") {
        setSelectValue({
          ...selectValue,
          shipping: {
            selected: `shipping-${address.id}`,
            label: joinAddress(address),
            value: address,
          },
        });
      } else {
        setSelectValue({
          ...selectValue,
          billing: {
            selected: `billing-${address.id}`,
            label: joinAddress(address),
            value: address,
          },
        });
      }
      setAddresses([...newAddresses, { ...address }]);
      setAddress(address);
    },
    [addresses, setAddresses, id, selectValue, setAddress, setSelectValue]
  );

  const handleShippingCheckboxChange = (e: boolean) => {
    setIsShippingPrimary(e);
    setIsBillingPrimary(!e);
  };

  const handleBillingCheckboxChange = (e: boolean) => {
    setIsBillingPrimary(e);
    setIsShippingPrimary(!e);
  };

  useEffect(() => {
    if (window.innerWidth < 640) {
      setIsShowMoreAddress(true);
    } else {
      setIsShowMoreAddress(false);
    }
  }, []);

  useEffect(() => {
    const handleWindowSize = () => {
      const width = window.innerWidth;

      if (width < 640) {
        setIsShowMoreAddress(true);
      } else {
        setIsShowMoreAddress(false);
      }
    };

    handleWindowSize();
  }, []);

  return (
    <>
      {loading && <TransparentLoader />}
      <div
        className={
          "relative pt-10 pb-14 px-14 max-xl:p-10 max-md:p-0 bg-gradient-to-b rounded-[2rem_6rem_6rem_6rem]"
        }
        id={`${id}-address`}
      >
        <img
          className="absolute top-0 left-0 size-full max-md:hidden"
          src={contactBorder}
          alt=""
        />
        <div className="relative">
          <div className="flex items-center flex-wrap justify-between max-md:items-start max-sm:mb-4">
            <h2 className="text-primary text-2xl max-md:text-xl">
              {title} Address
            </h2>

            {addressOptions.length !== 0 && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsAddNew(true);
                  }}
                  className="flex items-center gap-2 duration-150 hover:scale-95 text-2xl max-md:text-lg text-[#89D9EE] max-[375px]:text-base mr-4 max-md:mr-0 disabled:opacity-70"
                  aria-label="add-address-button"
                  disabled={addresses.length === 12}
                  data-tooltip-id={"add-address-type-checkout"}
                  data-tooltip-content={
                    addresses.length === 12
                      ? "You can add max 12 addresses."
                      : ""
                  }
                >
                  <i className="ri-edit-line"></i>Add Address
                </button>
                <Tooltip
                  style={{
                    backgroundColor: "#ffbff7",
                    color: "black",
                    zIndex: 99,
                    fontSize: 14,
                  }}
                  place="bottom-end"
                  id={`add-address-type-checkout`}
                />
              </>
            )}
          </div>

          {addressOptions.length !== 0 && (
            <div className="text-secondary text-base mt-6 max-md:mt-4 block f-family-mochiy-pop">
              Select from saved{" "}
              {addressOptions.length === 1 ? "address" : "addresses"}
            </div>
          )}

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-5 md:max-h-96 md:overflow-auto mt-7 max-md:mt-6">
              {(isShowMoreAddress
                ? addressOptions.slice(0, 2)
                : addressOptions
              ).map((option) => (
                <div
                  key={option.label}
                  className="relative cursor-pointer duration-150 hover:opacity-80"
                >
                  <img
                    className="absolute top-0 left-0 size-full sm:hidden"
                    src={shippingAddressBorder}
                    alt=""
                  />
                  <img
                    className="absolute top-0 left-0 size-full max-sm:hidden"
                    src={shippingBorder}
                    alt=""
                  />
                  <label
                    htmlFor={`${id}-${option.value.id}`}
                    className="text-base text-primary !flex items-center relative z-[4] gap-2 cursor-pointer w-full py-4 max-line-3 px-8"
                  >
                    <input
                      className="peer custom-radio-primary"
                      type="radio"
                      name={`${id}-${option.value.id}`}
                      id={`${id}-${option.value.id}`}
                      checked={
                        id == "shipping"
                          ? `shipping-${option.value.id}` ==
                            selectValue?.shipping?.selected
                          : `billing-${option.value.id}` ==
                            selectValue?.billing?.selected
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (id === "shipping") {
                            setSelectValue({
                              ...selectValue,
                              shipping: {
                                selected: `${id}-${option.value.id}`,
                                label: option.label,
                                value: option.value,
                              },
                            });
                          } else {
                            setSelectValue({
                              ...selectValue,
                              billing: {
                                selected: `${id}-${option.value.id}`,
                                label: option.label,
                                value: option.value,
                              },
                            });
                          }
                          setAddress({ ...option.value });
                        }
                      }}
                    />{" "}
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {(addressOptions.length === 0 || !user) && (
              <AddAddressWithout
                address={address}
                setAddress={setAddress}
                errors={errors}
                errorType={errorType}
                addresses={addresses}
                addressId={id}
                isShippingPrimary={isShippingPrimary}
                isBillingPrimary={isBillingPrimary}
                showBillingAddress={showBillingAddress}
              />
            )}
          </div>

          {addresses.length > 2 ? (
            <div className="flex justify-center w-full sm:hidden mb-5 mt-2">
              <button
                className="text-primary"
                onClick={() => {
                  setIsShowMoreAddress(!isShowMoreAddress);
                }}
              >
                Show {isShowMoreAddress ? "more" : "less"}
              </button>
            </div>
          ) : (
            <></>
          )}

          {user && addresses.length === 0 && (
            <div>
              {id === "shipping" ? (
                <div className="flex items-center gap-3 mt-3">
                  <input
                    className="peer custom-checkbox-primary"
                    type="checkbox"
                    id="isShippingPrimary"
                    checked={isShippingPrimary}
                    onChange={() =>
                      handleShippingCheckboxChange(!isShippingPrimary)
                    }
                    disabled={!showBillingAddress}
                  />
                  <label htmlFor="isShippingPrimary" className="text-primary">
                    MAKE THIS MY SHIPPING PRIMARY ADDRESS
                  </label>
                </div>
              ) : (
                <div className="flex items-center gap-3 mt-3">
                  <input
                    className="peer custom-checkbox-primary"
                    type="checkbox"
                    id="isBillingPrimary"
                    checked={isBillingPrimary}
                    onChange={() =>
                      handleBillingCheckboxChange(!isBillingPrimary)
                    }
                    // disabled={isPrimaryCheckboxDisable}
                  />
                  <label htmlFor="isBillingPrimary" className="text-primary">
                    MAKE THIS MY BILLING PRIMARY ADDRESS
                  </label>
                </div>
              )}

              {/* Billing Checkbox */}
            </div>
          )}
          {showBillingCheckbox && (
            <div className="relative py-4 cursor-pointer duration-150 hover:opacity-80 mb-5">
              <label
                htmlFor={id + "default-checkbox"}
                className="text-base text-primary flex items-center relative z-[4] gap-2"
              >
                <input
                  className="peer custom-checkbox-primary"
                  id={id + "default-checkbox"}
                  type="checkbox"
                  checked={showBillingAddress}
                  onChange={(e) => {
                    if (showBillingAddress) {
                      setIsShippingPrimary(true);
                      setIsBillingPrimary(false);
                    }
                    if (setBillingAddress) {
                      setBillingAddress(e.target.checked);
                    }
                  }}
                />
                Use a different billing address
              </label>
            </div>
          )}
        </div>
        <span className="text-red-500">
          {errors !== null && errors.type === errorType ? errors.message : null}
        </span>
        {isAddNew ? (
          <AddAddress
            updateAddresses={updateAddresses}
            title="New"
            closeModal={setIsAddNew}
            address={null}
            isEdit={false}
            ref={addAddressModalRef}
            id="checkout-address-form"
            addresses={addresses}
          />
        ) : null}
      </div>
    </>
  );
};

export default CheckoutAddress;
